export default {
  methods: {
    /**
     * 必須チェック
     * @param {string} value - チェックする値
     * @returns {boolean} - 必須条件を満たす場合は true
     */
    isRequired(value) {
      if (value === null || value === undefined) {
        return false; // 値が null または undefined の場合は false を返す
      }
      if (typeof value === 'string') {
        return value.trim() !== ''; // 文字列の場合は trim を使用して空チェック
      }
      if (Array.isArray(value)) {
        return value.length > 0; // 配列の場合は要素が存在するかをチェック
      }
      return true; // その他のデータ型（例えば数値）はそのまま true
    },

    /**
     * 桁数チェック
     * @param {string|number} value - チェックする値
     * @param {number} maxLength - 最大桁数
     * @returns {boolean} - 桁数条件を満たす場合は true
     */
    isMaxLength(value, maxLength) {
      if (value === null || value === undefined) return true; // 値が空の場合はチェック不要
      const strValue = String(value); // 数値を文字列に変換して桁数を判定
      return strValue.length <= maxLength;
    },

    /**
     * 日付の有効性チェック
     * @param {number|string} year - 年
     * @param {number|string} month - 月
     * @param {number|string} day - 日
     * @returns {boolean} - 有効な日付であれば true
     */
    isValidDate(year, month, day) {

      // 現在の年を取得
      const currentYear = new Date().getFullYear();

      // 数値に変換
      const y = parseInt(year, 10);
      const m = parseInt(month, 10);
      const d = parseInt(day, 10);

      // 変換結果がNaNの場合は無効
      if (isNaN(y) || isNaN(m) || isNaN(d)) {
        return false;
      }
      

      // 年が1～現在の年の範囲外であれば無効
      if ( y > currentYear) {
        return false;
      }

      // 月が1～12、日が1～31でない場合は無効
      if (m < 1 || m > 12 || d < 1 || d > 31) {
        return false;
      }

      // JavaScriptのDateオブジェクトで確認
      const date = new Date(y, m - 1, d); // 月は0始まり
      const isValid =
        date.getFullYear() === (y < 100 ? y + 1900 : y) && // 年の補正に対応
        date.getMonth() === m - 1 &&
        date.getDate() === d;

      return isValid;
    },

    /**
     * バリデーションチェック (形式チェック)
     * @param {string} field - フィールド名
     * @param {any} value - チェックする値
     * @returns {string} - エラーメッセージ (空文字列ならエラーなし)
     */
    validateFieldFormat(field, value) {
      // 空白の場合は形式チェックをスキップ
      if (!this.isRequired(value)) {
        return ''; // 必須条件を満たしていない場合はエラーなし
      }

      switch (field) {
        case 'email':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test(value) ? '' : '有効なメールアドレスを入力してください。';
        case 'zip':
          const zipRegex = /^\d{3}-\d{4}$/;
          return zipRegex.test(value) ? '' : '郵便番号は「123-4567」の形式で入力してください。';
        case 'fullNameKana':
          const kanaRegex = /^[ァ-ヴー\s]+$/;
          return kanaRegex.test(value) ? '' : '氏名（カナ）は全角カタカナで入力してください。';
        case 'password':
          const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;
          return passwordRegex.test(value)
            ? ''
            : 'パスワードは英数字を含む8文字以上で入力してください。';
        case 'passwordConfirm':
          return value === (this.newUser ? this.newUser.password : this.editUser?.password)
            ? ''
            : 'パスワードが一致しません。';
        case 'mobilePhone':
        case 'homePhone': // 両方で同じチェックを行う
          // ハイフンが含まれている場合はエラー
          if (value.includes('-')) {
            return `${field === 'mobilePhone' ? 'スマートフォン番号' : '固定電話番号'}はハイフンを含めず、10～11桁の数字で入力してください。`;
          }
      
          // 数字のみかつ10～11桁か確認
          const phoneRegex = /^\d{10,11}$/;
          return phoneRegex.test(value)
            ? ''
            : `${field === 'mobilePhone' ? 'スマートフォン番号' : '固定電話番号'}は10～11桁の数字を入力してください。`;
        case 'birthday.year':
          const year = parseInt(value, 10);
          if (isNaN(year) || year < 0) {
            return '年は正の数値を入力してください。';
          }
          return '';
        case 'birthday.month':
          const month = parseInt(value, 10);
          if (isNaN(month) || month < 1 || month > 12) {
            return '月は1～12の正の数値で入力してください。';
          }
          return '';
        case 'birthday.day':
          const day = parseInt(value, 10);
          if (isNaN(day) || day < 1 || day > 31) {
            return '日は1～31の正の数値で入力してください。';
          }
          return '';
        case 'birthday':
          // console.log('Validating birthday:', value); // デバッグ用
          const [fullYear, fullMonth, fullDay] = value.split('-').map(Number); // "-"で分割し数値化
          // 年月日のいずれかが入力されていない場合、有効な日付チェックをスキップ
          if (!fullYear || !fullMonth || !fullDay) {
            return ''; // 必須チェックでエラーが出るためここでは何もしない
          }
          if (!this.isValidDate(fullYear, fullMonth, fullDay)) {
            return '年月日は有効な日付を入力してください。';
          }
          return '';
        default:
          return ''; // 特に固有のチェックが必要ない場合
      }
    },

    /**
     * フォーム全体のバリデーションを実行
     * @param {Array} fields - 検証するフィールド情報の配列 [{ field: string, value: any, isRequired: boolean, maxLength: number }]
     * @param {Object} fieldLabels - フィールド名とラベルのマッピング
     * @returns {Object} - フィールドごとのエラーオブジェクト
     */
    validateForm(fields) {
      const errors = {};
      fields.forEach(({ field, label, value, isRequired, maxLength }) => {
        // console.log(`Validating field: ${field}`, { value, isRequired }); // デバッグ用

        // userTypeの固有チェック
        if (field === 'userType') {
          if (!value || value.trim() === '') {
            errors[field] = `${label}はどれか一つを選択してください。`;
            return; // 他のチェックは不要
          }
        }

        // 1. 必須チェック
        if (isRequired && !this.isRequired(value)) {
          errors[field] = `${label}は必須項目です。`;
          return; // 必須エラーがある場合は他のチェックをスキップ
        }

        // 2. 桁数チェック
        if (maxLength && !this.isMaxLength(value, maxLength)) {
          errors[field] = `${label}は${maxLength}文字以内で入力してください。`;
          return; // 桁数エラーがある場合は他のチェックをスキップ
        }

        // 3. バリデーションチェック (形式チェック)
        const formatError = this.validateFieldFormat(field, value);
        if (formatError) {
          errors[field] = formatError;
        }
      });
      return errors;
    },
  },
};
